import { ServiceRequestContext } from '@/context/serviceRequestContext';
import {
  capitalizeFirstLetter,
  checkTypeReinstall,
  formatedServiceDate,
  formatedServiceSlot,
  serviceTypeDisplayValue,
} from '@/utils/serviceRequest';
import { useContext } from 'react';
import { address } from '../SubPages/ServiceRequest';
import { orderObject } from '../SubPages/ServiceRequest/statusTracking';
import { combineAddressFields } from '@/utils/address';
import SessionStorage from '@/utils/sessionStorage';
import { getFormattedPrice } from '@/utils/price';

type serviceRequestInfoProps = {
  listClassNames?: string;
  listItemClassNames?: string;
  valueClassNames?: string;
  defaultAddress: address;
  order?: string[];
  statusTracking?: boolean;
};

const ServiceRequestInfo = (props: serviceRequestInfoProps) => {
  const {
    listClassNames,
    listItemClassNames,
    valueClassNames,
    defaultAddress,
    order,
    statusTracking,
  } = props;
  const { state: servicetState } = useContext(ServiceRequestContext);
  const {
    serviceType,
    defectType,
    addnRemarks,
    serviceDate,
    serviceSlot,
    address,
    isRescheduleFlow,
    prepaidAmountCollected,
  } = servicetState;
  const initialDefaultAddress = combineAddressFields(defaultAddress);
  const hideNewAddressField =
    !statusTracking && isRescheduleFlow === true && checkTypeReinstall(serviceType);

  const initialAddress =
    serviceType?.toLocaleLowerCase() !== 'reinstall'
      ? defaultAddress !== null
        ? initialDefaultAddress
        : ''
      : address === defaultAddress
      ? initialDefaultAddress
      : `${address.address1},`;

  const renderAddress = () => {
    return serviceType?.toLocaleLowerCase() === 'reinstall' && address !== defaultAddress ? (
      <span data-testid="address" className={`${valueClassNames} md:mt-1`}>
        {`${initialAddress} ${address.city ? `${address.city},` : ''} ${
          address.state ? `${address.state} ` : ' '
        } ${address.postal_code ? `${address.postal_code}` : ''}.`}
      </span>
    ) : (
      <span data-testid="address" className={`${valueClassNames} md:mt-1`}>
        {`${initialAddress} ${defaultAddress.city ? `${defaultAddress.city},` : ''} ${
          defaultAddress.state ? `${defaultAddress.state}` : ' '
        } ${defaultAddress.postal_code ? `${defaultAddress.postal_code}` : ''}.`}
      </span>
    );
  };

  const getServiceType = () => (
    <li className={listItemClassNames}>
      Service Type{' '}
      <span data-testid="serviceType" className={`${valueClassNames} md:mt-1`}>
        {serviceType?.toLowerCase() === 'ms'
          ? 'Mandatory Service'
          : serviceTypeDisplayValue(serviceType)}
      </span>
    </li>
  );

  const getDefectType = () =>
    serviceType?.toLowerCase() === 'repair' &&
    defectType && (
      <li className={listItemClassNames}>
        Defect Type
        <span data-testid="defectType" className={`${valueClassNames} md:mt-1`}>
          {capitalizeFirstLetter(defectType)}
        </span>
      </li>
    );

  const getAddress = () => {
    const newAddress = SessionStorage.get('address');
    if (newAddress) return newAddress;
    return (
      defaultAddress && (
        <li className={listItemClassNames}>
          {checkTypeReinstall(serviceType)
            ? statusTracking
              ? 'Current Address'
              : 'New Address'
            : 'Address'}
          {statusTracking
            ? typeof defaultAddress === 'string' && (
                <span data-testid="service-address" className={`${valueClassNames} md:mt-1`}>
                  {defaultAddress}
                </span>
              )
            : renderAddress()}
        </li>
      )
    );
  };
  const getScheduledDateAndTime = () =>
    serviceSlot?.slot.startTime &&
    serviceSlot?.slot.endTime && (
      <li className={listItemClassNames}>
        {statusTracking ? 'Preferred Date and Time' : 'Scheduled Date and Time'}
        <span data-testid="scheduledDateAndTime" className={`${valueClassNames} md:mt-1`}>
          {formatedServiceDate(serviceDate)},{' '}
          {serviceSlot &&
            formatedServiceSlot(
              new Date(serviceSlot?.slot.startTime),
              new Date(serviceSlot?.slot.endTime),
            )}
        </span>
      </li>
    );

  const getDescription = () =>
    (serviceType?.toLowerCase() === 'repair' || checkTypeReinstall(serviceType)) &&
    addnRemarks && (
      <li className={listItemClassNames}>
        Description
        <span data-testid="description" className={valueClassNames}>
          {addnRemarks}
        </span>
      </li>
    );

  const getPaymentStatus = () => {
    if (prepaidAmountCollected && Number(prepaidAmountCollected) > 0) {
      return (
        <li className={listItemClassNames}>
          Payment Status
          <span
            data-testid="paymentStatus"
            className={`${valueClassNames} md:mt-1 flex justify-between items-center self-stretch`}
          >
            <span>{getFormattedPrice(Number(prepaidAmountCollected))} Paid Online</span>
            {/* <span className="text-cu-selected">Download Invoice</span> */}
          </span>
        </li>
      );
    }
    return null;
  };

  const renderComponents = (key: string) => {
    switch (key) {
      case orderObject.dateAndTime:
        return getScheduledDateAndTime();
      case orderObject.serviceType:
        return getServiceType();
      case orderObject.defectType:
        return getDefectType();
      case orderObject.address:
        return getAddress();
      case orderObject.description:
        return getDescription();
      case orderObject.paymentStatus:
        return prepaidAmountCollected != null ? getPaymentStatus() : null;
      default:
        break;
    }
  };

  const returnInOrder = () => {
    return order?.map((key: string) => {
      return renderComponents(key);
    });
  };

  return order === undefined ? (
    <ul className={`${listClassNames}`}>
      {getServiceType()}
      {getDefectType()}
      {defectType === addnRemarks ? null : getDescription()}
      {getScheduledDateAndTime()}
      {!hideNewAddressField && getAddress()}
    </ul>
  ) : (
    <ul className={`${listClassNames}`}>{returnInOrder()}</ul>
  );
};
export default ServiceRequestInfo;
